import { useEffect, useState } from 'react';
import axios from 'axios';
import { s3cloudUrl, siteUrl, strapiAdminUrl } from 'src/api/config';
import { Modal } from 'src/components/DialogComponent';
import ImagesList from 'src/components/ImagesList';
import TGButton from 'src/components/ui/TGButton';
import { FlexRow } from 'src/utils/flexLayouts';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { isPWA } from "src/utils/mobile";

const isCloudLink = (str: string) => str.includes(s3cloudUrl);

const isImageUrl = (url: string) => {
  const u = url.toLowerCase();
  return u.indexOf('.jpg') !== -1 || u.indexOf('.jpeg') !== -1 || u.indexOf('.gif') !== -1 || u.indexOf('.png') !== -1;
};

const isDocUrl = (url: string) => {
  const u = url.toLowerCase();
  return u.indexOf('.docx') !== -1 || u.indexOf('.xlsx') !== -1;
};

export const isPreviewable = (url: string | undefined): boolean =>
  !!url && (isImageUrl(url) || isDocUrl(url));

export function useFilePreview(backendUrl: string, previewBaseUrl: string = backendUrl) {
  const [viewDocMode, setViewDocMode] = useState(false);
  const [imagesDoc, setImagesDoc] = useState<string[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewFileId, setPreviewFileId] = useState<number | null>(null);

  const buildFullUrl = (url: string) => {
    if (/^(https?:)?\/\//i.test(url)) return url;

    return isCloudLink(url)
      ? `${backendUrl}/${new URL(url).pathname.slice(1)}`
      : `${backendUrl}${url}`;
  }


  useEffect(() => {
    if (imagesDoc.length > 0) {
      setViewDocMode(true);
    }
  }, [imagesDoc]);

  const openPreview = async (url: string, fileId?: number) => {
    let token: string | null = null;

    if (!isPWA()) {
      const result = await SecureStoragePlugin.get({ key: 'accessToken' });
      token = result.value;
    }

    const headers = {
      Accept: "application/json;odata=nometadata",
      "Content-Type": "application/json;odata=nometadata",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    if (!url) return;
    if (isImageUrl(url)) {
      setImagesDoc([buildFullUrl(url)]);
    } else if (isDocUrl(url)) {
      if (!fileId) return;
      setPreviewFileId(fileId);
      setPreviewLoading(true);
      const fullUrl = buildFullUrl(url);
      axios
        .post(
          `${siteUrl}/api/md-documents-files/preview_path`,
          { id: fileId, path: fullUrl.replace(siteUrl, strapiAdminUrl) },
          { headers, withCredentials: true },
        )
        .then((results) => {
          const { data } = results;
          setImagesDoc(data.map((u: string) => `${previewBaseUrl}${u}`));
        })
        .finally(() => {
          setPreviewLoading(false);
          setPreviewFileId(null);
        });
    }
  };

  const PreviewModal = (
    <Modal
      zIndex={140}
      modalClassName="min-h-px flex gap-5 p-10"
      open={viewDocMode}
      toggleDialog={setViewDocMode}
    >
      <ImagesList images={imagesDoc} />
      <FlexRow className="w-full justify-center items-center">
        <TGButton
          onClick={() => {
            setImagesDoc([]);
            setViewDocMode(false);
          }}
          color="bluedark"
        >
          Закрыть
        </TGButton>
      </FlexRow>
    </Modal>
  );

  return { openPreview, previewLoading, previewFileId, PreviewModal };
}
